<script setup lang="ts">
import { Check, X, Video, Users, GraduationCap, Crown, CheckCircle } from 'lucide-vue-next'
import { router } from '@inertiajs/vue3'
import { computed } from 'vue'

interface Subscription {
  teacher_id: number
  tier: string
  expires_in: number | null
}

interface Props {
  tutorSlug: string
  tutorId?: number
  basePrice: number
  silverPrice: number
  goldPrice: number
  servicesOffered: string[]
  videosCount: number
  subjects: Array<{ id: number; name: string }>
  groupSubjects?: Array<{ id: number; name: string; levels: Array<{ id: number; name: string }> }>
  subscriptions?: Subscription[]
}

const props = withDefaults(defineProps<Props>(), {
  groupSubjects: () => [],
  subscriptions: () => [],
})

// Tier slug to plan ID mapping for subscription matching
const tierToPlanId: Record<string, string> = {
  base: 'content',
  silver: 'group',
  gold: 'oneOnOne',
}

const activeSub = computed(() =>
  props.subscriptions.find((s) => s.teacher_id === props.tutorId)
)

const isSubscribed = computed(() => !!activeSub.value)

const subscribedTier = computed(() => activeSub.value?.tier ?? null)

function isSubscribedToPlan(planId: string): boolean {
  if (!subscribedTier.value) return false
  const planTier = { content: 'base', group: 'silver', oneOnOne: 'gold' }[planId]
  return subscribedTier.value === planTier
}

// Check what services are offered
const offersVideos = props.servicesOffered.includes('videos') || props.servicesOffered.includes('materials')
const offersGroupSessions = props.servicesOffered.includes('group_sessions')
const offersOneOnOne = props.servicesOffered.includes('one_on_one')

// Build descriptive text for group session subjects/levels
function groupSubjectText(): string {
  const gs = props.groupSubjects
  if (!gs || gs.length === 0) return 'Choose your subject'

  if (gs.length === 1) {
    const subject = gs[0]
    if (subject.levels.length === 0) return `Choose ${subject.name}`
    if (subject.levels.length === 1) return `Choose ${subject.name} ${subject.levels[0].name}`
    if (subject.levels.length === 2) return `Choose ${subject.name} ${subject.levels.map((l) => l.name).join(' or ')}`
    const first = subject.levels.slice(0, 2).map((l) => l.name).join(', ')
    return `Choose from ${subject.name} ${first} & more`
  }

  return `Choose ${gs.map((s) => s.name).join(' or ')}`
}

// Map plan IDs to tier slugs
const planToTierSlug: Record<string, string> = {
  content: 'base',
  group: 'silver',
  oneOnOne: 'gold',
}

const plans = [
  {
    id: 'content',
    name: 'Content Library',
    price: props.basePrice,
    priceLabel: '/week',
    description: 'Perfect for self-learners who want access to comprehensive study materials',
    icon: Video,
    bgGradient: 'from-gray-50 to-gray-100',
    borderColor: 'border-gray-200',
    hoverBorderColor: 'hover:border-gray-400',
    iconBgColor: 'bg-white',
    iconColor: 'text-gray-600',
    checkColor: 'text-gray-600',
    buttonGradient: 'bg-gray-600',
    buttonHoverGradient: 'hover:bg-gray-700',
    offered: offersVideos && props.basePrice > 0,
    features: [
      `${props.videosCount}+ study videos`,
      'Downloadable materials',
      'Mobile app access',
      'Email support (48h)',
    ],
  },
  {
    id: 'group',
    name: 'Group Tuition',
    price: props.silverPrice,
    priceLabel: '/week/subject',
    priceNote: 'Subscribe per subject',
    description: 'Great for active learners who want interactive group sessions',
    icon: Users,
    bgGradient: 'from-blue-50 to-blue-100',
    borderColor: 'border-blue-400',
    hoverBorderColor: 'hover:border-blue-600',
    iconBgColor: 'bg-white',
    iconColor: 'text-blue-600',
    checkColor: 'text-blue-600',
    buttonGradient: 'bg-gradient-to-r from-blue-600 to-blue-700',
    buttonHoverGradient: 'hover:from-blue-700 hover:to-blue-800',
    offered: offersGroupSessions && props.silverPrice > 0,
    popular: true,
    badge: {
      text: 'POPULAR',
      gradient: 'bg-gradient-to-r from-blue-600 to-blue-700',
    },
    features: [
      'Access to Content Library',
      '1 group session per week',
      groupSubjectText(),
      'Subject-specific group chat',
    ],
  },
  {
    id: 'oneOnOne',
    name: '1:1 Tuition',
    price: props.goldPrice,
    priceLabel: '/week',
    description: 'Best for serious students seeking personalized attention',
    icon: GraduationCap,
    bgGradient: 'from-yellow-50 to-orange-50',
    borderColor: 'border-yellow-400',
    hoverBorderColor: 'hover:border-yellow-600',
    iconBgColor: 'bg-white',
    iconColor: 'text-yellow-600',
    checkColor: 'text-yellow-600',
    buttonGradient: 'bg-gradient-to-r from-yellow-500 to-orange-500',
    buttonHoverGradient: 'hover:from-yellow-600 hover:to-orange-600',
    offered: offersOneOnOne && props.goldPrice > 0,
    premium: true,
    badge: {
      text: 'PREMIUM',
      gradient: 'bg-gradient-to-r from-yellow-500 to-orange-500',
    },
    features: [
      'Access to Content Library',
      '1 one-to-one session/week',
      'Personalized study plan',
      'Direct messaging (24h)',
    ],
  },
]

function handleSubscribe(planId: string) {
  if (isSubscribed.value) return
  const tierSlug = planToTierSlug[planId]
  router.visit(`/payment?tutor=${props.tutorSlug}&tier=${tierSlug}`)
}
</script>

<template>
  <div>
    <h3 class="text-xl font-semibold text-gray-900 mb-4">Membership Plans Available</h3>

    <!-- Pricing Cards -->
    <div class="flex flex-col md:grid grid-cols-3 gap-4 mb-6">
      <div
        v-for="plan in plans"
        :key="plan.id"
        :class="[
          'p-5 rounded-xl border-2 flex flex-col relative',
          plan.offered
            ? `bg-gradient-to-br ${plan.bgGradient} ${plan.borderColor} ${plan.hoverBorderColor} transition-all`
            : 'bg-gray-200 border-gray-300 opacity-75',
        ]"
      >
        <!-- Badge -->
        <div v-if="plan.badge && plan.offered" class="absolute -top-2 left-1/2 transform -translate-x-1/2">
          <span :class="['text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1', plan.badge.gradient]">
            <Crown v-if="plan.premium" class="w-3 h-3" />
            {{ plan.badge.text }}
          </span>
        </div>

        <!-- Not Offered Badge -->
        <div v-if="!plan.offered" class="absolute -top-2 left-1/2 transform -translate-x-1/2">
          <span class="bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-bold">NOT OFFERED</span>
        </div>

      <div class="flex items-center gap-4 md:block">

        <!-- Icon -->
        <div :class="[
          'w-8 h-8 md:w-12 md:h-12 rounded-xl flex items-center justify-center mb-3',
          plan.badge ? 'mt-2' : '',
          plan.offered ? plan.iconBgColor : 'bg-gray-300'
        ]">
          <component :is="plan.icon" :class="[
            'w-4 h-4 md:w-6 md:h-6',
            plan.offered ? plan.iconColor : 'text-gray-500'
          ]" />
        </div>

        <!-- Plan Name -->
        <h4 :class="['font-semibold mb-2 flex-grow-1', plan.offered ? 'text-gray-900' : 'text-gray-600']">
          {{ plan.name }}
        </h4>

        <!-- Price -->
        <div v-if="plan.offered" class="mb-3">
          <div :class="['text-xl md:text-2xl font-bold', plan.iconColor]">
            £{{ Number(plan.price).toFixed(2) }}<span class="text-sm text-gray-600 font-normal">{{ plan.priceLabel }}</span>
          </div>
          <p v-if="plan.priceNote" class="text-xs text-gray-500 italic">
            <b>{{ plan.priceNote }}</b>
          </p>
        </div>
        <div v-else class="mb-3">
          <div class="text-2xl font-bold text-gray-500">N/A</div>
        </div>

      </div>

        <!-- Description -->
        <p :class="['text-sm mb-3', plan.offered ? 'text-gray-600' : 'text-gray-500']">
          {{ plan.description }}
        </p>

        <!-- Features -->
        <div class="space-y-2 mb-4 flex-grow">
          <div v-for="(feature, idx) in plan.features" :key="idx" :class="[
            'flex items-center text-xs',
            plan.offered ? 'text-gray-700' : 'text-gray-500'
          ]">
            <Check :class="['mr-2 w-4 h-4', plan.offered ? plan.checkColor : 'text-gray-400']" />
            <span>{{ feature }}</span>
          </div>
        </div>

        <!-- Subscribe Button -->
        <button
          v-if="plan.offered && isSubscribedToPlan(plan.id)"
          class="w-full bg-green-600 text-white py-2.5 rounded-lg font-semibold mt-auto cursor-default flex items-center justify-center gap-2"
          disabled
        >
          <CheckCircle class="w-4 h-4" />
          Subscribed
        </button>
        <button
          v-else-if="plan.offered && isSubscribed"
          class="w-full bg-gray-400 text-white py-2.5 rounded-lg font-semibold mt-auto cursor-not-allowed"
          disabled
        >
          Already Subscribed
        </button>
        <button
          v-else-if="plan.offered"
          @click="handleSubscribe(plan.id)"
          :class="['w-full text-white py-2.5 rounded-lg font-semibold transition-all mt-auto cursor-pointer', plan.buttonGradient, plan.buttonHoverGradient]"
        >
          Subscribe to Plan
        </button>
        <button v-else class="w-full bg-gray-400 text-white py-2.5 rounded-lg font-semibold mt-auto cursor-not-allowed" disabled>
          Not Available
        </button>
      </div>
    </div>

    <!-- Features Comparison Table -->
    <div class="bg-gray-50 rounded-2xl p-4">
      <h4 class="text-base font-semibold text-gray-900 mb-3 text-center">Plan Features Comparison</h4>
      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="border-b border-gray-300">
              <th class="text-left py-2 px-3 text-gray-700 font-semibold text-sm">Features</th>
              <th class="text-center py-2 px-3 text-gray-600 font-semibold text-sm">Content Library</th>
              <th class="text-center py-2 px-3 text-blue-600 font-semibold text-sm">Group Tuition</th>
              <th class="text-center py-2 px-3 text-yellow-700 font-semibold text-sm">1:1 Tuition</th>
            </tr>
          </thead>
          <tbody class="text-xs">
            <tr class="border-b border-gray-200">
              <td class="py-2 px-3 text-gray-700 font-medium">{{ videosCount }}+ Study Videos</td>
              <td class="text-center py-2 px-3">
                <Check v-if="offersVideos" class="w-4 h-4 text-gray-600 inline" />
                <X v-else class="w-4 h-4 text-gray-400 inline" />
              </td>
              <td class="text-center py-2 px-3">
                <Check v-if="offersGroupSessions" class="w-4 h-4 text-blue-600 inline" />
                <X v-else class="w-4 h-4 text-gray-400 inline" />
              </td>
              <td class="text-center py-2 px-3">
                <Check v-if="offersOneOnOne" class="w-4 h-4 text-yellow-600 inline" />
                <X v-else class="w-4 h-4 text-gray-400 inline" />
              </td>
            </tr>
            <tr class="border-b border-gray-200">
              <td class="py-2 px-3 text-gray-700 font-medium">Downloadable Materials</td>
              <td class="text-center py-2 px-3">
                <Check v-if="offersVideos" class="w-4 h-4 text-gray-600 inline" />
                <X v-else class="w-4 h-4 text-gray-400 inline" />
              </td>
              <td class="text-center py-2 px-3">
                <Check v-if="offersGroupSessions" class="w-4 h-4 text-blue-600 inline" />
                <X v-else class="w-4 h-4 text-gray-400 inline" />
              </td>
              <td class="text-center py-2 px-3">
                <Check v-if="offersOneOnOne" class="w-4 h-4 text-yellow-600 inline" />
                <X v-else class="w-4 h-4 text-gray-400 inline" />
              </td>
            </tr>
            <tr class="border-b border-gray-200">
              <td class="py-2 px-3 text-gray-700 font-medium">Email Support (48h)</td>
              <td class="text-center py-2 px-3">
                <Check v-if="offersVideos" class="w-4 h-4 text-gray-600 inline" />
                <X v-else class="w-4 h-4 text-gray-400 inline" />
              </td>
              <td class="text-center py-2 px-3">
                <Check v-if="offersGroupSessions" class="w-4 h-4 text-blue-600 inline" />
                <X v-else class="w-4 h-4 text-gray-400 inline" />
              </td>
              <td class="text-center py-2 px-3">
                <Check v-if="offersOneOnOne" class="w-4 h-4 text-yellow-600 inline" />
                <X v-else class="w-4 h-4 text-gray-400 inline" />
              </td>
            </tr>
            <tr class="border-b border-gray-200 bg-blue-50">
              <td class="py-2 px-3 text-gray-700 font-medium">Group Sessions (1/week per subject)</td>
              <td class="text-center py-2 px-3"><X class="w-4 h-4 text-gray-400 inline" /></td>
              <td class="text-center py-2 px-3">
                <Check v-if="offersGroupSessions" class="w-4 h-4 text-blue-600 inline" />
                <X v-else class="w-4 h-4 text-gray-400 inline" />
              </td>
              <td class="text-center py-2 px-3"><X class="w-4 h-4 text-gray-400 inline" /></td>
            </tr>
            <tr class="bg-yellow-50">
              <td class="py-2 px-3 text-gray-700 font-medium">1:1 Sessions (1/week)</td>
              <td class="text-center py-2 px-3"><X class="w-4 h-4 text-gray-400 inline" /></td>
              <td class="text-center py-2 px-3"><X class="w-4 h-4 text-gray-400 inline" /></td>
              <td class="text-center py-2 px-3">
                <Check v-if="offersOneOnOne" class="w-4 h-4 text-yellow-600 inline" />
                <X v-else class="w-4 h-4 text-gray-400 inline" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
